import type { DecisionExecutionDTO } from '@deepguide-ai/types'
import { Link } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import {
  DetailStatusGrid,
  DetailStatusItem,
} from '@/components/compliance/detail-primitives'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'

function requestOutcomeLabel(status: string): string {
  const labels: Record<string, string> = {
    AWAITING_HUMAN: 'Waiting for a decision',
    RUNNING: 'In progress',
    COMPLETED: 'Completed',
    FAILED: 'Needs attention',
    CANCELLED: 'Cancelled',
  }
  return labels[status] ?? 'In progress'
}

function policyGuidanceLabel(policy: {
  title: string
  policyDocumentVersion: number | null
  citations: unknown[]
}): string {
  const title = policy.title.replace(/^\[[^\]]+\]\s*/, '')
  return `${title}${
    policy.policyDocumentVersion != null
      ? ` v${policy.policyDocumentVersion}`
      : ''
  } · ${policy.citations.length} relevant statement${
    policy.citations.length === 1 ? '' : 's'
  }`
}

export function hasDecisionRequestContext(
  decision: DecisionExecutionDTO | null | undefined,
): boolean {
  return Boolean(
    decision?.requestContext ||
      decision?.requestContextHash ||
      decision?.policyBasis,
  )
}

export function DecisionExecutionPanel({
  decision,
  workspaceId,
}: {
  decision: DecisionExecutionDTO | null | undefined
  workspaceId?: string
}) {
  if (!decision) return null
  const context = decision.requestContext
  if (!hasDecisionRequestContext(decision)) return null
  const outcomeLabel =
    decision.outcome === 'fulfilled'
      ? 'Completed'
      : decision.outcome === 'denied'
        ? 'Not approved'
        : decision.outcome === 'blocked'
          ? 'Needs attention'
          : requestOutcomeLabel(decision.status)
  return (
    <DetailStatusGrid className='grid-cols-2 gap-x-8 gap-y-4 !max-w-full'>
      <DetailStatusItem label='Request subject'>
        {context?.subject?.email ? (
          <span className='font-body inline-flex items-center gap-1.5 text-[13px]'>
            <MemberAvatar
              size='xs'
              person={{
                name: context.subject.email,
                email: context.subject.email,
              }}
            />
            {context.subject.email}
          </span>
        ) : (
          <span className='font-body text-[13px]'>
            No employee was identified
          </span>
        )}
      </DetailStatusItem>
      {/* <DetailStatusItem label='Audit reference'>
        <span className='font-mono text-[11px]  break-all'>
          {decision.requestContextHash ?? 'Not available'}
        </span>
      </DetailStatusItem> */}
      <DetailStatusItem label='Policy guidance'>
        {decision.policyBasis?.policies.length ? (
          <div className='space-y-1'>
            {decision.policyBasis.policies.map((policy) => {
              const label = policyGuidanceLabel(policy)
              return workspaceId && policy.policySlug ? (
                <Link
                  key={policy.policyDocumentId}
                  to='/workspaces/$workspaceId/policies/$policySlug'
                  params={{ workspaceId, policySlug: policy.policySlug }}
                  className='font-body inline-flex items-center gap-1 text-[13px] text-primary underline-offset-2 hover:underline'
                >
                  {label}
                  <ExternalLink
                    className='h-3.5 w-3.5 shrink-0 text-status-blue'
                    aria-hidden
                  />
                </Link>
              ) : (
                <span
                  key={policy.policyDocumentId}
                  className='font-body block text-[13px]'
                >
                  {label}
                </span>
              )
            })}
          </div>
        ) : (
          <span className='font-body text-[13px]'>
            No policy guidance was saved with this request
          </span>
        )}
      </DetailStatusItem>
      <DetailStatusItem label='Policy match'>
        {decision.policyBasis ? (
          <div className='space-y-1'>
            <div className='text-[13px] font-medium text-status-green tabular-nums'>
              {Math.round(decision.policyBasis.confidence * 100)}% match
            </div>
            <p className='font-body text-[13px] text-text-subtle'>
              {decision.policyBasis.rationale}
            </p>
          </div>
        ) : (
          <span className='font-body text-[13px]'>Not available</span>
        )}
      </DetailStatusItem>
    </DetailStatusGrid>
  )
}
